import React, { useState, useEffect, useContext } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { useTheme } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Container from '@material-ui/core/Container'
import ReactBSAlert from 'react-bootstrap-sweetalert'
import Grid from '@material-ui/core/Grid'
import FormGroup from '@material-ui/core/FormGroup'
import FormControl from '@material-ui/core/FormControl'
import FormLabel from '@material-ui/core/FormLabel'
import Checkbox from '@material-ui/core/Checkbox'
import Button from '@material-ui/core/Button'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import Loader from 'components/general/Loader'
import AdminAPI from 'config/api/AdminAPI'
import EnergyAPI from 'config/api/EnergyAPI'
import { onError } from 'config/lib/errorLib'
import LeadsTable from 'components/admin/LeadsTable'
import EnergyLeadsTable from 'components/admin/Energy/LeadsTable'
import componentStyles from 'assets/theme/views/admin/alternative-dashboard.js'
import componentStylesCardDeck from 'assets/theme/components/cards/card-deck.js'
import { getMoments, getProductString } from 'config/helpers/Helpers'
import { buildCapacityMap } from 'config/helpers/AllocationCapacity'
import Settings from '@material-ui/icons/Settings'
import moment from 'moment-timezone'
import PeriodSelect from 'components/general/PeriodSelect'
import ActiveProductsModal from 'components/admin/ActiveProductsModal'

const useStyles = makeStyles(componentStyles)
const useStylesCardDeck = makeStyles(componentStylesCardDeck)

function Leads() {
    const classes = { ...useStyles(), ...useStylesCardDeck() }
    const theme = useTheme()
    const api = new AdminAPI()
    const energyApi = new EnergyAPI()

    const [leads, setLeads] = useState([])
    const [showLeads, setShowLeads] = useState([])
    const [showStatus, setShowStatus] = useState('Unallocated')
    const [period, setPeriod] = useState('thisWeek')
    const [sourceOptions, setSourceOptions] = useState([])
    const [companyOptions, setCompanyOptions] = useState([])
    const [source, setSource] = useState('all')
    const [county, setCounty] = useState('all')
    const [countyOptions, setCountyOptions] = useState([])
    const [reactivationOnly, setReactivationOnly] = useState(false)
    const [product, setProduct] = useState('pensions')
    const [fromDate, setFromDate] = useState('')
    const [toDate, setToDate] = useState('')
    const [company, setCompany] = useState('all')
    const [companies, setCompanies] = useState([])
    const [solarCompanies, setSolarCompanies] = useState([])
    const [fsDeliveries, setFsDeliveries] = useState([])
    const [capacityMap, setCapacityMap] = useState({})
    const [energyDeliveries, setEnergyDeliveries] = useState([])
    // const [loadMore, setLoadMore] = useState(false)
    // const [moreLeadsLoading, setMoreLeadsLoading] = useState(false)
    const [createdOnly, setCreatedOnly] = useState(false)
    const [loading, setLoading] = useState(true)
    const [alert, setAlert] = useState(false)
    const [productsAvailable, setProductsAvailable] = useState([])
    const [showActiveProducts, setShowActiveProducts] = useState(false)

    // Period, Product, and Company are top-row filters - tracked as pending until Search is clicked
    const [pendingPeriod, setPendingPeriod] = useState('thisWeek')
    const [pendingFromDate, setPendingFromDate] = useState('')
    const [pendingToDate, setPendingToDate] = useState('')
    const [pendingProduct, setPendingProduct] = useState('pensions')
    const [pendingCompany, setPendingCompany] = useState('all')

    useEffect(() => {
        getLeads()
    }, [])

    async function getLeads() {
        try {
            let dates = getMoments(period)

            let productsAvailable = await api.getProductsAvailable()
            console.log('products available: ', productsAvailable.productsAvailable)

            let leads = await api.getAllLeadsPeriod(product, dates.fromDate.valueOf(), dates.toDate.valueOf())
            let sourceOptions = new Set()
            let companyOptions = new Set()
            let countyOptions = new Set()
            leads.sort((a, b) => {
                sourceOptions.add(a.source)
                if (a.companyId) companyOptions.add(a.companyId)
                if (a.county) countyOptions.add(a.county)
                return b.createdAt - a.createdAt
            })
            console.log('leads size: ', leads.length)
            console.log('source options: ', sourceOptions)
            
            // The business week (Sat–Fri, Europe/Dublin) is what the caps are
            // measured against, regardless of the period being viewed.
            const weekDates = getMoments('thisWeek')
            const [companies, solarCompanies, fsDeliveriesRaw, weekSummary, energyDeliveriesRaw] = await Promise.all([
                api.getCompanies(),
                energyApi.getCompanies(),
                // one call covering weekly + balance + hidden (HV / pension-advice) deliveries
                api.getFSDeliveries(),
                api.getFSLeadsPeriod(weekDates.fromDate.valueOf(), weekDates.toDate.valueOf(), null),
                energyApi.getEnergyDeliveries(),
            ])
            console.log('companies: ', companies)
            const activeCompanies = companies.filter((company) => (company.active))
            activeCompanies.sort((a, b) => {
                return a.name.localeCompare(b.name)
            })

            console.log('solarCompanies: ', solarCompanies)
            const activeSolarCompanies = solarCompanies.filter((company) => (company.active))
            activeSolarCompanies.sort((a, b) => {
                return a.name.localeCompare(b.name)
            })

            setCompanies(activeCompanies)
            setSolarCompanies(activeSolarCompanies)
            setFsDeliveries(fsDeliveriesRaw)
            setCapacityMap(buildCapacityMap(fsDeliveriesRaw, weekSummary.deliveryCounts))
            setEnergyDeliveries(energyDeliveriesRaw)
            setCompanyOptions(companyOptions)
            setLeads(leads)
            setShowLeads(leads.filter((lead) => (lead.companyId === null && lead.isValid)))
            setFromDate(dates.fromDate)
            setToDate(dates.toDate)
            setPendingFromDate(dates.fromDate)
            setPendingToDate(dates.toDate)
            setSourceOptions(sourceOptions)
            setCountyOptions(countyOptions)
            //setLoadMore(response.loadMore)
            setProductsAvailable(productsAvailable.productsAvailable)
            setLoading(false)
        } catch(e) {
            onError(e)
        }
    }

    async function fetchLeads(product, fromDate, toDate) {
        let leads = []
        if (product === 'all') {
            for (let p of Object.keys(productsAvailable)) {
                if (productsAvailable[p]) {
                    let productLeads = await api.getAllLeadsPeriod(p, fromDate.valueOf(), toDate.valueOf())
                    leads = leads.concat(productLeads)
                }
            }
        }
        else if (product === 'Solar') {
            let eeLeads = await energyApi.getEeLeadsPeriod(product, fromDate.valueOf(), toDate.valueOf())
            let sfLeads = await energyApi.getSfLeadsPeriod(product, fromDate.valueOf(), toDate.valueOf())
            leads = eeLeads.concat(sfLeads)
        }
        else if (['Insulation', 'EV Charger', 'Boiler', 'Heat Pump', 'Commercial Solar'].indexOf(product) > -1) {
            leads = await energyApi.getEeLeadsPeriod(product, fromDate.valueOf(), toDate.valueOf())
        }
        else {
            leads = await api.getAllLeadsPeriod(product, fromDate.valueOf(), toDate.valueOf())
        }
        return leads
    }

    function filterLeads(leadsArr, showStatus, source, company, createdOnly, fromDate, toDate, county = 'all', reactivationOnly = false) {
        let filtered = []
        if (showStatus === 'Unallocated') {
            filtered = leadsArr.filter((lead) => lead.companyId === null && lead.isValid)
        }
        else if (showStatus === 'Allocated') {
            filtered = leadsArr.filter((lead) => lead.companyId !== null)
        }
        else if (showStatus === 'Rejected') {
            filtered = leadsArr.filter((lead) => lead.companyId === null && !lead.isValid)
        }
        if (source !== 'all') {
            filtered = filtered.filter((lead) => lead.source === source)
        }
        if (company !== 'all') {
            filtered = filtered.filter((lead) => lead.companyId === company)
        }
        if (county !== 'all') {
            filtered = filtered.filter((lead) => lead.county === county)
        }
        if (reactivationOnly) {
            filtered = filtered.filter((lead) => lead.isReactivation)
        }
        if (createdOnly) {
            filtered = filtered.filter((lead) => moment(lead.createdAtOriginal).isBetween(moment(fromDate), moment(toDate)))
        }
        return filtered
    }

    function handlePendingPeriod(newPeriod, customFromDate=null, customToDate=null) {
        setPendingPeriod(newPeriod)
        if (newPeriod === 'custom') {
            setPendingFromDate(moment(customFromDate).startOf('day'))
            setPendingToDate(moment(customToDate).endOf('day'))
        }
        else {
            const dates = getMoments(newPeriod)
            setPendingFromDate(dates.fromDate)
            setPendingToDate(dates.toDate)
        }
    }

    async function applyFilters() {
        const dates = pendingPeriod === 'custom'
            ? { fromDate: moment(pendingFromDate).startOf('day'), toDate: moment(pendingToDate).endOf('day') }
            : getMoments(pendingPeriod)

        // If a specific company is chosen, default to Allocated since unallocated leads have no company
        const initialStatus = pendingCompany !== 'all' ? 'Allocated' : showStatus

        setLoading(true)
        try {
            const newLeads = await fetchLeads(pendingProduct, dates.fromDate, dates.toDate)
            let sourceOpts = new Set()
            let companyOpts = new Set()
            let countyOpts = new Set()
            newLeads.sort((a, b) => {
                sourceOpts.add(a.source)
                if (a.companyId) companyOpts.add(a.companyId)
                if (a.county) countyOpts.add(a.county)
                return b.createdAt - a.createdAt
            })
            const filtered = filterLeads(newLeads, initialStatus, 'all', pendingCompany, false, dates.fromDate, dates.toDate, 'all', false)
            setLeads(newLeads)
            setShowLeads(filtered)
            setSourceOptions(sourceOpts)
            setCompanyOptions(companyOpts)
            setCountyOptions(countyOpts)
            setPeriod(pendingPeriod)
            setProduct(pendingProduct)
            setCompany(pendingCompany)
            setShowStatus(initialStatus)
            setSource('all')
            setCounty('all')
            setReactivationOnly(false)
            setCreatedOnly(false)
            setFromDate(dates.fromDate)
            setToDate(dates.toDate)
            setLoading(false)
        } catch(e) {
            onError(e)
            setLoading(false)
        }
    }

    function updateLead(lead) {
        const index = leads.findIndex(oldLead => oldLead.leadId === lead.leadId);
        console.log('lead index: ', index);

        let newLeads = [...leads];
        newLeads[index] = lead;
        console.log(newLeads);

        // Optimize filtering with memoization
        const updatedShowLeads = (() => {
            if (showStatus === 'Unallocated') {
                return newLeads.filter(lead => lead.companyId === null && lead.isValid);
            } else if (showStatus === 'Allocated') {
                return newLeads.filter(lead => lead.companyId !== null);
            } else if (showStatus === 'Rejected') {
                return newLeads.filter(lead => lead.companyId === null && !lead.isValid);
            }
            return [];
        })();

        console.log('show leads: ', updatedShowLeads);

        // Batch state updates to prevent multiple renders
        setLeads(newLeads);
        setShowLeads(updatedShowLeads);
    }

    // function updateLeads(leads) {
    //     let newLeads = [...leads]
    //     for (let lead of leads) {
    //         const index = leads.findIndex(oldLead => (oldLead.leadId === lead.leadId))
    //         console.log('lead index: ', index)

    //         newLeads[index] = lead
    //     }
    //     //setLeads(newLeads.filter((lead) => lead.companyId === null))
    //     let showLeads = []
    //     if (showStatus === 'Unallocated') {
    //         showLeads = newLeads.filter((lead) => lead.companyId === null && lead.isValid)
    //     }
    //     else if (showStatus === 'Allocated') {
    //         showLeads = newLeads.filter((lead) => lead.companyId !== null)
    //     }
    //     else if (showStatus === 'Rejected') {
    //         showLeads = newLeads.filter((lead) => lead.companyId === null && !lead.isValid)
    //     }
    //     console.log('show leads: ', showLeads)
    //     setShowLeads(showLeads)
    //     setLeads(newLeads)
    // }

    function removeLeads(leadIds) {
        const newLeads = leads.filter(lead => !leadIds.includes(lead.leadId))
        setLeads(newLeads)
        setShowLeads(filterLeads(newLeads, showStatus, source, company, createdOnly, fromDate, toDate, county, reactivationOnly))
    }

    /*
     * Every company actively receiving this product, however they buy it —
     * weekly delivery, prepaid balance, or one of the hardcoded/hidden
     * deliveries (HV / pension-advice accounts). Previously pensions looked at
     * balance deliveries only, which is why weekly pension buyers were missing
     * from the dropdown until 'Show all companies' was clicked.
     */
    function getFilteredCompanies(product) {
        // For 'all' product view, no meaningful filter - return everything
        if (product === 'all') return companies
        const activeIds = new Set(
            fsDeliveries
                .filter(d => d.active !== false && d.product === product)
                .map(d => d.companyId)
        )
        return companies.filter(c => activeIds.has(c.companyId))
    }

    function showSuccessAlert(text) {
        setAlert(
            <ReactBSAlert
                success
                style={{ display: 'block', marginTop: '-100px' }}
                title='Success'
                onConfirm={() => setAlert(false)}
                onCancel={() => setAlert(false)}
                confirmBtnBsStyle='success'
                confirmBtnText='Ok'
                btnSize=''
                confirmBtnStyle={{
                    marginRight: undefined,
                    borderColor: undefined,
                    boxShadow: undefined,
                }}
            >
                {text}
            </ReactBSAlert>
        )
    }

    const pendingDates = pendingPeriod === 'custom'
        ? { fromDate: pendingFromDate ? moment(pendingFromDate).startOf('day') : null, toDate: pendingToDate ? moment(pendingToDate).endOf('day') : null }
        : getMoments(pendingPeriod)
    const hasPendingChange = pendingProduct !== product
        || pendingPeriod !== period
        || pendingCompany !== company
        || (pendingDates.fromDate && fromDate && pendingDates.fromDate.valueOf() !== moment(fromDate).valueOf())
        || (pendingDates.toDate && toDate && pendingDates.toDate.valueOf() !== moment(toDate).valueOf())

    // Companies available for the currently-selected pending product
    const isEnergyProduct = ['Solar', 'Insulation', 'EV Charger', 'Boiler', 'Heat Pump', 'Commercial Solar'].indexOf(pendingProduct) > -1
    const companyOptionsForProduct = isEnergyProduct ? solarCompanies : companies

    return (
        <>
            {alert}
            <Container
                maxWidth={false}
                component={Box}
                //marginTop='-6rem'
                marginTop='2rem'
                classes={{ root: classes.containerRoot }}
            >
                {!loading ? (
                <Grid container>
                    <Grid item xs={12}>
                        <Box display='flex' direction='row' justifyContent='flex-end'>
                            <Settings onClick={() => setShowActiveProducts(true)} style={{color: '#525f7f', width: '20px', height: '20px', cursor: 'pointer', marginBottom: '5px'}} />
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Grid
                            container
                            component={Box}
                            alignItems='flex-start'
                            justifyContent='flex-start'
                            style={{marginBottom: '1.5rem'}}
                            direction='row'
                            spacing={2}
                        >
                            <Grid item xs={12} md={2}>
                                <PeriodSelect
                                    period={pendingPeriod}
                                    updatePeriod={(p, fd, td) => handlePendingPeriod(p, fd, td)}
                                    fromDate={pendingFromDate || fromDate}
                                    toDate={pendingToDate || toDate}
                                />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <FormGroup style={{marginBottom: '0'}}>
                                    <FormControl variant='outlined'>
                                        <Select
                                            multiple={false}
                                            defaultValue={null}
                                            value={pendingProduct}
                                            onChange={(e) => {
                                                setPendingProduct(e.target.value)
                                                setPendingCompany('all')
                                            }}
                                        >
                                            <MenuItem value={'all'}>Financial (All)</MenuItem>
                                            <MenuItem value={'Solar'}>Solar (All)</MenuItem>
                                            <MenuItem value={'Insulation'}>Insulation</MenuItem>
                                            <MenuItem value={'EV Charger'}>EV Charger</MenuItem>
                                            <MenuItem value={'Boiler'}>Boiler</MenuItem>
                                            <MenuItem value={'Heat Pump'}>Heat Pump</MenuItem>
                                            <MenuItem value={'Commercial Solar'}>Commercial Solar</MenuItem>
                                            {Object.keys(productsAvailable).map((key) => {
                                                if (productsAvailable[key]) {
                                                    return (<MenuItem value={key}>{getProductString(key)}</MenuItem>)
                                                }
                                            })}
                                        </Select>
                                    </FormControl>
                                </FormGroup>
                                <Box
                                    fontSize='12px'
                                    color={theme.palette.gray[600]}
                                    marginTop='0.5rem'
                                    textAlign='left'
                                >
                                    <Box component='small' fontSize='100%'>
                                        Product
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormGroup style={{marginBottom: '0'}}>
                                    <FormControl variant='outlined' fullWidth>
                                        <Select
                                            multiple={false}
                                            value={pendingCompany}
                                            onChange={(e) => setPendingCompany(e.target.value)}
                                            displayEmpty
                                        >
                                            <MenuItem value={'all'}>All Companies</MenuItem>
                                            {companyOptionsForProduct.map((c) => (
                                                <MenuItem key={c.companyId} value={c.companyId}>{c.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </FormGroup>
                                <Box fontSize='12px' color={theme.palette.gray[600]} marginTop='0.5rem' textAlign='left'>
                                    <Box component='small' fontSize='100%'>Company</Box>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <Button
                                    component={Box}
                                    onClick={() => applyFilters()}
                                    color='primary'
                                    variant={hasPendingChange ? 'contained' : 'outlined'}
                                    size='medium'
                                    disabled={!hasPendingChange}
                                >
                                    Search
                                </Button>
                                {hasPendingChange &&
                                    <Box fontSize='12px' color={theme.palette.warning.main} marginTop='0.5rem' textAlign='left'>
                                        <Box component='small' fontSize='100%'>
                                            Click search to load
                                        </Box>
                                    </Box>
                                }
                            </Grid>
                        </Grid>
                        {!hasPendingChange &&
                            <Grid
                                container
                                component={Box}
                                alignItems='flex-start'
                                justifyContent='flex-start'
                                style={{marginBottom: '2rem'}}
                                direction='row'
                                spacing={2}
                            >
                                <Grid item xs={12} md={2}>
                                    <FormGroup style={{marginBottom: '0'}}>
                                        <FormControl variant='outlined' fullWidth>
                                            <Select
                                                multiple={false}
                                                value={showStatus}
                                                onChange={(e) => {
                                                    const newStatus = e.target.value
                                                    // If switching away from Allocated, drop company/createdOnly since they don't apply
                                                    const newCompany = newStatus === 'Allocated' ? company : 'all'
                                                    const newCreatedOnly = newStatus === 'Allocated' ? createdOnly : false
                                                    const filtered = filterLeads(leads, newStatus, source, newCompany, newCreatedOnly, fromDate, toDate, county, reactivationOnly)
                                                    setShowLeads(filtered)
                                                    setShowStatus(newStatus)
                                                    setCompany(newCompany)
                                                    setCreatedOnly(newCreatedOnly)
                                                }}
                                            >
                                                <MenuItem value={'Unallocated'}>Unallocated</MenuItem>
                                                <MenuItem value={'Allocated'}>Allocated</MenuItem>
                                                <MenuItem value={'Rejected'}>Rejected</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </FormGroup>
                                    <Box fontSize='12px' color={theme.palette.gray[600]} marginTop='0.5rem' textAlign='left'>
                                        <Box component='small' fontSize='100%'>Status</Box>
                                    </Box>
                                </Grid>
                                {sourceOptions.size > 0 &&
                                    <Grid item xs={12} md={2}>
                                        <FormGroup style={{marginBottom: '0'}}>
                                            <FormControl variant='outlined'>
                                                <Select
                                                    multiple={false}
                                                    value={source}
                                                    onChange={(e) => {
                                                        const newSource = e.target.value
                                                        const filtered = filterLeads(leads, showStatus, newSource, company, createdOnly, fromDate, toDate, county, reactivationOnly)
                                                        setShowLeads(filtered)
                                                        setSource(newSource)
                                                    }}
                                                >
                                                    <MenuItem value={'all'}>All</MenuItem>
                                                    {[...sourceOptions].map((option) => {
                                                        return (<MenuItem value={option}>{option}</MenuItem>)
                                                    })}
                                                </Select>
                                            </FormControl>
                                        </FormGroup>
                                        <Box fontSize='12px' color={theme.palette.gray[600]} marginTop='0.5rem' textAlign='left'>
                                            <Box component='small' fontSize='100%'>Source</Box>
                                        </Box>
                                    </Grid>
                                }
                                {(product === 'Solar' || product === 'Insulation' || product === 'EV Charger' || product === 'Boiler' || product === 'Heat Pump' || product === 'Commercial Solar') && countyOptions.size > 0 &&
                                    <Grid item xs={12} md={2}>
                                        <FormGroup style={{marginBottom: '0'}}>
                                            <FormControl variant='outlined'>
                                                <Select
                                                    multiple={false}
                                                    value={county}
                                                    onChange={(e) => {
                                                        const newCounty = e.target.value
                                                        const filtered = filterLeads(leads, showStatus, source, company, createdOnly, fromDate, toDate, newCounty, reactivationOnly)
                                                        setShowLeads(filtered)
                                                        setCounty(newCounty)
                                                    }}
                                                >
                                                    <MenuItem value={'all'}>All</MenuItem>
                                                    {[...countyOptions].sort().map((option) => {
                                                        return (<MenuItem value={option}>{option}</MenuItem>)
                                                    })}
                                                </Select>
                                            </FormControl>
                                        </FormGroup>
                                        <Box fontSize='12px' color={theme.palette.gray[600]} marginTop='0.5rem' textAlign='left'>
                                            <Box component='small' fontSize='100%'>County</Box>
                                        </Box>
                                    </Grid>
                                }
                                {product === 'Solar' &&
                                    <Grid item xs={12} md={2}>
                                        <FormGroup style={{marginBottom: '0'}}>
                                            <FormControl variant='outlined'>
                                                <Select
                                                    multiple={false}
                                                    value={reactivationOnly ? 'reactivation' : 'all'}
                                                    onChange={(e) => {
                                                        const newReactivationOnly = e.target.value === 'reactivation'
                                                        const filtered = filterLeads(leads, showStatus, source, company, createdOnly, fromDate, toDate, county, newReactivationOnly)
                                                        setShowLeads(filtered)
                                                        setReactivationOnly(newReactivationOnly)
                                                    }}
                                                >
                                                    <MenuItem value={'all'}>All leads</MenuItem>
                                                    <MenuItem value={'reactivation'}>Reactivation only</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </FormGroup>
                                        <Box fontSize='12px' color={theme.palette.gray[600]} marginTop='0.5rem' textAlign='left'>
                                            <Box component='small' fontSize='100%'>SMS Reactivation</Box>
                                        </Box>
                                    </Grid>
                                }
                                {showStatus === 'Allocated' &&
                                    <Grid item xs={12} md={3} style={{display: 'flex', alignItems: 'center'}}>
                                        <Checkbox
                                            color='primary' 
                                            checked={createdOnly} 
                                            onChange={(e) => {
                                                const newCreatedOnly = e.target.checked
                                                const filtered = filterLeads(leads, showStatus, source, company, newCreatedOnly, fromDate, toDate, county, reactivationOnly)
                                                setShowLeads(filtered)
                                                setCreatedOnly(newCreatedOnly)
                                            }}
                                        />
                                        <FormLabel style={{margin: '0', padding: '0'}}>Only show created for period</FormLabel>
                                    </Grid>
                                }
                            </Grid>
                        }
                    </Grid>
                    <Grid item xs={12}> 
                        {(product === 'Solar' || product === 'Insulation' || product === 'EV Charger' || product === 'Boiler' || product === 'Heat Pump' || product === 'Commercial Solar') ? (
                            <EnergyLeadsTable
                                leads={showLeads}
                                title={showStatus}
                                refreshLeads={() => getLeads()}
                                updateLead={(lead) => updateLead(lead)}
                                showSuccessAlert={(text) => showSuccessAlert(text)}
                                companies={solarCompanies}
                                energyDeliveries={energyDeliveries}
                                allSolarCompanies={solarCompanies}
                                product={product}
                                //site='sf'
                            />
                        ) : (
                            <LeadsTable
                                leads={showLeads}
                                title={showStatus}
                                refreshLeads={() => getLeads()}
                                updateLeads={(leads) => updateLead(leads)}
                                updateLead={(lead) => updateLead(lead)}
                                showSuccessAlert={(text) => showSuccessAlert(text)}
                                companies={companies}
                                filteredCompanies={getFilteredCompanies(product)}
                                allCompanies={companies}
                                capacityMap={capacityMap}
                                product={product}
                                showStatus={showStatus}
                                removeLeads={removeLeads}
                            />
                        )}
                    </Grid>

                    {showActiveProducts && 
                        <ActiveProductsModal
                            isOpen={showActiveProducts}
                            close={() => setShowActiveProducts(false)}
                            products={productsAvailable}
                            updateProducts={(products) => setProductsAvailable(products)}
                            //showSuccessAlert={(text) => props.showSuccessAlert(text)}
                        />
                    }
                </Grid>
                ) : (
                    <Grid container>
                        <Grid item xs={12}>
                            <Box display='flex' justifyContent='center' alignItems='center' style={{height: '20vh'}}>
                                <Loader />
                            </Box>
                        </Grid>
                    </Grid>
                )}
            </Container>
        </>
    );
}

export default Leads